import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import SafetyEventsData from "../../data/other-safety-events.data";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    fontFamily: "Helvetica",
    fontSize: 11,
    paddingTop: 30,
    paddingLeft: 50,
    paddingRight: 50,
    lineHeight: 1.5,
    flexDirection: "column",
  },
  logo: {
    width: 84,
    height: 70,
    marginLeft: "auto",
    marginRight: "auto",
  },
  titleContainer: {
    marginTop: 24,
  },
  reportTitle: {
    color: "#3778C2",
    letterSpacing: 4,
    fontSize: 25,
    textAlign: "center",
    textTransform: "uppercase",
  },
  reportDescription: {
    fontSize: 12,
    marginBottom: 10,
  },
  row: {
    flexDirection: "column",
    borderBottomWidth: 1,
    fontSize: 12,
    fontStyle: "bold",
  },
  description: {
    width: "100%",
  },
  total: {
    width: "100%",
  },
});

export type HealthEventFormTypes = {
  formData: {
    eventDate: string;
    eventLocation: string;
    area: string;
    description: string;
    immediateConsequences: string;
    consequencesHad: string;
    professionalNames: string;
    supervisionRateRespected: string;
    usualTeam: string;
    immediateCorrectiveAction: string;
    riskRelatedEvent: string;
    eventAvoidedSaferEquipment: string;
    recurrence: string;
    origin: string;
    mistreatmentEvidence: string;
    insuranceBeenNotified: string;
    localAuthorityBeenNotified: string;
    printeventDate: string;
    printprofessionalNames: boolean;
    printeventLocation: string;
    printarea: string;
    printdescription: string;
    printimmediateConsequences: string;
    printconsequencesHad: string;
    printsupervisionRateRespected: string;
    printusualTeam: string;
    printimmediateCorrectiveAction: string;
    printriskRelatedEvent: string;
    printeventAvoidedSaferEquipment: string;
    printrecurrence: string;
    printorigin: string;
    printmistreatmentEvidence: string;
    printinsuranceBeenNotified: string;
    printlocalAuthorityBeenNotified: string;
  };
};

export const SafetyEventsPDF = ({ formData }: HealthEventFormTypes) => {
  const { t } = useTranslation();
  const formatRistRelatedData = (data: string) => {
    return data.split("-,-").map((element, index) => {
      return (
        <View key={element} style={{ flexDirection: "row", marginBottom: 4 }}>
          <Text style={{ marginHorizontal: 8 }}> {++index} - </Text>
          <Text>{t(element)}.</Text>
          <Text style={{ minWidth: "100%" }}></Text>
        </View>
      );
    });
  };
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.titleContainer}>
          <Text style={styles.reportTitle}>{t(SafetyEventsData.title)}</Text>
          <Text style={styles.reportDescription}>
            {t(SafetyEventsData.description.i18nKey)}
          </Text>
        </View>
        {SafetyEventsData.fields.map((field, index) => {
          if (field.name === "eventDate" && formData["printeventDate"]) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["eventDate"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "eventLocation" &&
            formData["printeventLocation"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["eventLocation"])}
                </Text>
              </View>
            );
          } else if (field.name === "origin" && formData["printorigin"]) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>{t(formData["origin"])}</Text>
              </View>
            );
          } else if (
            field.name === "mistreatmentEvidence" &&
            formData["printmistreatmentEvidence"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["mistreatmentEvidence"])}
                </Text>
              </View>
            );
          } else if (field.name === "area" && formData["printarea"]) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>{t(formData["area"])}</Text>
              </View>
            );
          } else if (
            field.name === "description" &&
            formData["printdescription"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["description"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "immediateConsequences" &&
            formData["printimmediateConsequences"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {formatRistRelatedData(formData["immediateConsequences"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "immediateCorrectiveAction" &&
            formData["printimmediateCorrectiveAction"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {formatRistRelatedData(formData["immediateCorrectiveAction"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "insuranceBeenNotified" &&
            formData["printinsuranceBeenNotified"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["insuranceBeenNotified"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "localAuthorityBeenNotified" &&
            formData["printlocalAuthorityBeenNotified"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["localAuthorityBeenNotified"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "supervisionRateRespected" &&
            formData["printsupervisionRateRespected"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["supervisionRateRespected"])}
                </Text>
              </View>
            );
          } else if (field.name === "usualTeam" && formData["printusualTeam"]) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["usualTeam"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "professionalNames" &&
            formData["printprofessionalNames"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["professionalNames"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "riskRelatedEvent" &&
            formData["printriskRelatedEvent"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {/* {formData["riskRelatedEvent"]} */}
                  {formatRistRelatedData(formData["riskRelatedEvent"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "eventAvoidedSaferEquipment" &&
            formData["printeventAvoidedSaferEquipment"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["eventAvoidedSaferEquipment"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "consequencesHad" &&
            formData["printconsequencesHad"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["consequencesHad"])}
                </Text>
              </View>
            );
          } else if (
            field.name === "recurrence" &&
            formData["printrecurrence"]
          ) {
            return (
              <View key={index} style={styles.row}>
                <Text style={styles.total}>{t(field.label)}</Text>
                <Text style={styles.description}>
                  {t(formData["recurrence"])}
                </Text>
              </View>
            );
          }
          return null;
        })}
      </Page>
    </Document>
  );
};
